// src/components/ProtectedLevelRoute.jsx
// ✅ V9 - Premium check now reads the `passes` table directly, matching
//         usePremiumStatus.js exactly (same table, same filter, same helper).
//         The previous `subscriptions` table was the wrong table — server
//         code writes to `passes`, so reading `subscriptions` always failed.
//
// Gating:
//   Level 1     → no login required (AuthModal inside Level-1 handles sign-in)
//   Levels 2–3  → login required, unlimited free play
//   Levels 4–10 → login + active pass in `passes` table

import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { hasActivePass } from "../services/passStorage";
import { isActivePassRow } from "../hooks/usePremiumStatus";
import PianoLoadingScreen from "./PianoLoadingScreen";

const MODE = (process.env.REACT_APP_PWP_PAYWALL_MODE || "mock").toLowerCase();
const IS_MOCK_MODE = !(MODE === "razorpay" || MODE === "live");

const PREMIUM_ONLY_THRESHOLD = 4; // Levels 4+ require premium (1–3 are free)

export default function ProtectedLevelRoute({ children, levelId }) {
  const location = useLocation();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;

    async function run() {
      try {
        const current = location.pathname + (location.search || "");
        const isPremiumLevel = levelId >= PREMIUM_ONLY_THRESHOLD;

        // ── 1) Get session ──
        const { data } = await supabase.auth.getSession();
        const session = data?.session;

        // ── NOT LOGGED IN ──
        if (!session?.access_token) {
          if (!alive) return;

          // Level 1: allow through — AuthModal inside Level-1 handles sign-in
          if (levelId === 1) {
            setState({ loading: false, allowed: true });
            return;
          }

          // Levels 2+: must log in first
          setState({ loading: false, toPaywall: true, next: current, reason: "login" });
          return;
        }

        // ── LOGGED IN: levels 1–3 → free, no further checks ──
        if (!isPremiumLevel) {
          if (!alive) return;
          setState({ loading: false, allowed: true });
          return;
        }

        // ── LEVELS 4–10: check premium status ──
        let hasPremium = false;

        if (IS_MOCK_MODE) {
          // Dev mode: localStorage-backed pass (same as usePremiumStatus mock branch)
          hasPremium = hasActivePass();
        } else {
          // Real mode: read latest active pass for this user from `passes`.
          // Same query shape as /api/pwp-entitlement and usePremiumStatus.js —
          // guarantees menu and level guard never disagree about premium.
          try {
            const nowIso = new Date().toISOString();
            const { data: passRow, error: passErr } = await supabase
              .from("passes")
              .select("plan_id, status, start_at, expires_at")
              .eq("user_id", session.user.id)
              .eq("status", "active")
              .gt("expires_at", nowIso)
              .order("expires_at", { ascending: false })
              .limit(1)
              .maybeSingle();

            if (passErr) {
              console.warn("⚠️ ProtectedLevelRoute: passes query error:", passErr.message);
              hasPremium = false;
            } else {
              hasPremium = isActivePassRow(passRow);
              if (!hasPremium && passRow) {
                console.warn(
                  "⚠️ ProtectedLevelRoute: passes row returned but not active. Row:",
                  passRow
                );
              }
            }
          } catch (err) {
            console.warn("⚠️ ProtectedLevelRoute: passes check threw:", err.message);
            hasPremium = false;
          }
        }

        if (!alive) return;

        if (hasPremium) {
          setState({ loading: false, allowed: true });
        } else {
          setState({ loading: false, toPaywall: true, next: current, reason: "premium_required" });
        }

      } catch (e) {
        console.error("❌ ProtectedLevelRoute error:", e);
        if (!alive) return;
        const current = location.pathname + (location.search || "");

        // Failsafe for mock mode: allow free levels through if session exists
        if (IS_MOCK_MODE) {
          try {
            const { data } = await supabase.auth.getSession();
            const hasSession = Boolean(data?.session?.access_token);
            if (hasSession && levelId < PREMIUM_ONLY_THRESHOLD) {
              setState({ loading: false, allowed: true });
              return;
            }
          } catch {}
        }

        setState({ loading: false, toPaywall: true, next: current, error: e?.message });
      }
    }

    run();
    return () => { alive = false; };
  }, [location.pathname, location.search, levelId]);

  if (state.loading) {
    return <PianoLoadingScreen message="Checking access" />;
  }

  if (state.toPaywall) {
    const next = state.next || location.pathname + (location.search || "");
    const reason = state.reason || "premium_required";
    return <Navigate to={`/pwp-paywall?next=${encodeURIComponent(next)}&reason=${reason}`} replace />;
  }

  return children;
}